import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Recursively traverses React nodes to highlight matching string fragments
export function highlightText(children, query) {
  if (!query) return children;

  return React.Children.map(children, (child) => {
    if (typeof child === "string") {
      if (!child.toLowerCase().includes(query.toLowerCase())) return child;

      // Split by query, keeping the delimiter
      const parts = child.split(new RegExp(`(${query})`, "gi"));
      return parts.map((part, index) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark
            key={index}
            className="bg-primary/20 text-primary rounded-[2px] font-medium bg-transparent"
          >
            {part}
          </mark>
        ) : (
          part
        ),
      );
    }

    if (React.isValidElement(child) && child.props.children) {
      return React.cloneElement(child, {
        ...child.props,
        children: highlightText(child.props.children, query),
      });
    }

    return child;
  });
}

/**
 * Shared changelog-flavoured markdown renderer. Styles both the app
 * CHANGELOG (### Added/Fixed/Changed sections → coloured badges) and the
 * firmware release notes (- **Added**: bullets). Used by ChangelogModal and
 * the firmware release history (TimerFirmware).
 */
export default function MarkdownBlock({ content, searchQuery }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h3: ({ node, children, ...props }) => {
          const text = String(children).toLowerCase();
          let colourClass = "bg-muted text-muted-foreground";

          if (text.includes("added")) {
            colourClass =
              "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400";
          } else if (text.includes("fixed")) {
            colourClass = "bg-blue-500/10 text-blue-600 dark:text-blue-400";
          } else if (text.includes("changed") || text.includes("improved")) {
            colourClass = "bg-amber-500/10 text-amber-600 dark:text-amber-400";
          }

          return (
            <h4
              className={`mt-6 mb-3 inline-block rounded px-2 py-1 text-[11px] font-bold uppercase tracking-widest ${colourClass}`}
              {...props}
            >
              {children}
            </h4>
          );
        },
        p: ({ node, children, ...props }) => (
          <p
            className="text-sm leading-6 text-muted-foreground mb-2 last:mb-0"
            {...props}
          >
            {highlightText(children, searchQuery)}
          </p>
        ),
        ul: ({ node, children, ...props }) => (
          <ul
            className="ml-5 list-disc space-y-1.5 text-sm text-muted-foreground mb-4 last:mb-0"
            {...props}
          >
            {children}
          </ul>
        ),
        ol: ({ node, children, ...props }) => (
          <ol
            className="ml-5 list-decimal space-y-1.5 text-sm text-muted-foreground mb-4 last:mb-0"
            {...props}
          >
            {children}
          </ol>
        ),
        li: ({ node, children, ...props }) => (
          <li className="pl-1 leading-relaxed" {...props}>
            {highlightText(children, searchQuery)}
          </li>
        ),
        pre: ({ node, children, ...props }) => (
          <pre
            className="block overflow-x-auto rounded-md bg-muted p-3 my-4 border border-border/50 [&>code]:bg-transparent [&>code]:p-0"
            {...props}
          >
            {children}
          </pre>
        ),
        code: ({ className, children, ...props }) => (
          <code
            className={`font-mono text-[13px] text-foreground rounded bg-muted/80 border border-border/50 px-1 py-0.5 ${className || ""}`.trim()}
            {...props}
          >
            {children}
          </code>
        ),
        a: ({ node, children, ...props }) => (
          <a
            className="text-primary font-medium underline underline-offset-4 hover:opacity-80 transition-opacity"
            target="_blank"
            rel="noreferrer"
            {...props}
          >
            {children}
          </a>
        ),
        strong: ({ node, children, ...props }) => (
          <strong className="font-semibold text-foreground" {...props}>
            {children}
          </strong>
        ),
      }}
    >
      {content || "No changelog notes yet."}
    </ReactMarkdown>
  );
}
